import React, { createContext, useContext, ReactNode } from 'react';
import { useTenantConfig } from './useTenantConfig';

// Default translations for multi-tenant support
const TRANSLATIONS: Record<string, Record<string, string>> = {
  'es-GT': {
    // Common actions
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Agregar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.import': 'Importar',
    'common.close': 'Cerrar',
    'common.yes': 'Sí',
    'common.no': 'No',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    
    // Navigation
    'nav.dashboard': 'Panel de Control',
    'nav.sales': 'Ventas',
    'nav.inventory': 'Inventario',
    'nav.customers': 'Clientes',
    'nav.suppliers': 'Proveedores',
    'nav.reports': 'Reportes',
    'nav.settings': 'Configuración',
    
    // Status labels
    'status.active': 'Activo',
    'status.inactive': 'Inactivo',
    'status.pending': 'Pendiente',
    'status.completed': 'Completado',
    'status.cancelled': 'Cancelado',
    
    // Validation messages
    'validation.required': 'Este campo es requerido',
    'validation.email': 'Ingresa un email válido',
    'validation.minLength': 'Mínimo {min} caracteres',
    'validation.maxLength': 'Máximo {max} caracteres',
    'validation.numeric': 'Solo números permitidos',
    'validation.positive': 'Debe ser un número positivo',
    
    // Business terms
    'business.nit': 'NIT',
    'business.cui': 'CUI',
    'business.taxId': 'Identificación Tributaria',
    'business.invoice': 'Factura',
    'business.receipt': 'Recibo',
    'business.tax': 'Impuesto',
    'business.subtotal': 'Subtotal',
    'business.total': 'Total',
    'business.discount': 'Descuento',
    
    // Security
    'security.accessDenied': 'Acceso denegado',
    'security.authRequired': 'Autenticación requerida',
    'security.companyRequired': 'Selecciona una empresa',
    'security.insufficientPermissions': 'Permisos insuficientes',
    
    // Multi-tenant
    'tenant.selectCompany': 'Seleccionar Empresa',
    'tenant.noCompanies': 'No tienes empresas asignadas',
    'tenant.switchCompany': 'Cambiar Empresa',
    'tenant.companyInfo': 'Información de la Empresa',
  },
  
  'en-US': {
    // Common actions
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.sales': 'Sales',
    'nav.inventory': 'Inventory',
    'nav.customers': 'Customers',
    'nav.suppliers': 'Suppliers',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    
    // Status labels
    'status.active': 'Active',
    'status.inactive': 'Inactive',
    'status.pending': 'Pending',
    'status.completed': 'Completed',
    'status.cancelled': 'Cancelled',
    
    // Validation messages
    'validation.required': 'This field is required',
    'validation.email': 'Enter a valid email',
    'validation.minLength': 'Minimum {min} characters',
    'validation.maxLength': 'Maximum {max} characters',
    'validation.numeric': 'Only numbers allowed',
    'validation.positive': 'Must be a positive number',
    
    // Business terms
    'business.nit': 'Tax ID',
    'business.cui': 'Personal ID',
    'business.taxId': 'Tax Identification',
    'business.invoice': 'Invoice',
    'business.receipt': 'Receipt',
    'business.tax': 'Tax',
    'business.subtotal': 'Subtotal',
    'business.total': 'Total',
    'business.discount': 'Discount',
    
    // Security
    'security.accessDenied': 'Access denied',
    'security.authRequired': 'Authentication required',
    'security.companyRequired': 'Select a company',
    'security.insufficientPermissions': 'Insufficient permissions',
    
    // Multi-tenant
    'tenant.selectCompany': 'Select Company',
    'tenant.noCompanies': 'No companies assigned',
    'tenant.switchCompany': 'Switch Company',
    'tenant.companyInfo': 'Company Information',
  },
  
  'es-MX': {
    // Common actions
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Agregar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.import': 'Importar',
    'common.close': 'Cerrar',
    'common.yes': 'Sí',
    'common.no': 'No',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    
    // Navigation
    'nav.dashboard': 'Tablero',
    'nav.sales': 'Ventas',
    'nav.inventory': 'Inventario',
    'nav.customers': 'Clientes',
    'nav.suppliers': 'Proveedores',
    'nav.reports': 'Reportes',
    'nav.settings': 'Configuración',
    
    // Status labels
    'status.active': 'Activo',
    'status.inactive': 'Inactivo',
    'status.pending': 'Pendiente',
    'status.completed': 'Completado',
    'status.cancelled': 'Cancelado',
    
    // Validation messages
    'validation.required': 'Este campo es obligatorio',
    'validation.email': 'Ingresa un correo válido',
    'validation.minLength': 'Mínimo {min} caracteres',
    'validation.maxLength': 'Máximo {max} caracteres',
    'validation.numeric': 'Solo números permitidos',
    'validation.positive': 'Debe ser un número positivo',
    
    // Business terms
    'business.nit': 'RFC',
    'business.cui': 'CURP',
    'business.taxId': 'Identificación Fiscal',
    'business.invoice': 'Factura',
    'business.receipt': 'Recibo',
    'business.tax': 'Impuesto',
    'business.subtotal': 'Subtotal',
    'business.total': 'Total',
    'business.discount': 'Descuento',
    
    // Security
    'security.accessDenied': 'Acceso denegado',
    'security.authRequired': 'Autenticación requerida',
    'security.companyRequired': 'Selecciona una empresa',
    'security.insufficientPermissions': 'Permisos insuficientes',
    
    // Multi-tenant
    'tenant.selectCompany': 'Seleccionar Empresa',
    'tenant.noCompanies': 'No tienes empresas asignadas',
    'tenant.switchCompany': 'Cambiar Empresa',
    'tenant.companyInfo': 'Información de la Empresa',
  }
};

interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  locale: string;
  currency: string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const { config } = useTenantConfig();

  const t = (key: string, params?: Record<string, string | number>): string => {
    const translations = TRANSLATIONS[config.locale] || TRANSLATIONS['es-GT'];
    let translation = translations[key] || key;

    // Replace parameters in translation
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{${paramKey}}`, String(paramValue));
      });
    }

    return translation;
  };

  const isRTL = ['ar', 'he', 'fa'].some(lang => config.locale.startsWith(lang));

  return (
    <I18nContext.Provider value={{
      t,
      locale: config.locale,
      currency: config.currency,
      isRTL
    }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}

// Convenience hook for just the translate function
export function useT() {
  const { t } = useTranslation();
  return t;
}